import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {TieredMenu} from "primereact/tieredmenu";
import {Toast} from "primereact/toast";
import {useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";

import {selectByLanguage} from "@reva/shared/i18n";
import {isLifeCycleAtLeast, RevaLifeCycleState} from "@reva/shared/lifecycle-state";
import {SnapshotType} from "@reva/shared/valuation-data";

import {WebError} from "../../api/web-error";
import AppreciationToolbarButton from "../../component/appreciation/AppreciationToolbarButton";
import ReopenForCorrectionDialog from "../../component/ReopenForCorrectionDialog";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useCheckPermission} from "../../hooks/permissionHooks";
import {useCanStartCorrection, useLiveDataEntry, useLoadValuationState} from "../../hooks/valuationLoadHooks";
import {selectUsername} from "../../redux/authSlice";
import {selectDocuments} from "../../redux/documentsSlice";
import {downloadDocumentAsync} from "../../redux/documentsThunk";
import {selectHasAnyParamConflicts} from "../../redux/objectParametersSelectors";
import {selectParameterConflicts, selectSegment} from "../../redux/objectParametersSlice";
import {ValuationPdfType} from "../../redux/pdfConstants";
import {fetchSnapshotPdf, fetchValuationPdf} from "../../redux/pdfThunk";
import {
    selectCorrectionCounter,
    selectIsSnapshot,
    selectLifeCycleState,
    selectLifeCycleStateFinal,
    selectLoadedSnapshotNumber,
    selectPersistenceSingleParamError,
    selectPurposePolicy,
    selectReadOnly,
    selectRevaId,
    selectSnapshots,
    selectValuationMetadata,
} from "../../redux/persistenceSlice";
import {selectApiInputParamsForCurrentSegment} from "../../redux/specSelectors";
import {selectAnyValueAvailable} from "../../redux/valueIndicationSelectors";
import {selectMostRelevantExternalOrderNumber} from "../../redux/valueOrdersSelectors";
import {openBlobForDownload} from "../../util/browserUtil";
import {formatDate, formatTime, isSameDay} from "../../util/dateUtil";
import {getErrorDetail} from "../../util/errorUtil";
import {findFinalizedDocument} from "../../util/finalizedDocument";
import {
    PERMISSION_RULE_CORRECTION,
    PERMISSION_RULE_FINALIZATION,
    PERMISSION_RULE_PRE_FINALIZATION,
} from "../../util/permissions";
import {useAllowedArchiveDocumentTypes} from "./ArchiveDocumentsSelection";
import DialogFinalizeValuation from "./DialogFinalizeValuation";
import DialogPreFinalizeValuation from "./DialogPreFinalizeValuation";
import {isPdfSupported} from "./pdfSupport";

/**
 * Erklärender Zusatz am Lebenszyklus-Menü, wenn (Vor-)Finalisieren nicht möglich ist. Die Konflikte
 * stehen als Parameter-Schlüssel im Store; den Anzeigenamen holt erst diese Berechnung aus der Spec,
 * und ohne Eintrag dort bleibt der Schlüssel stehen.
 * @param {object} params
 * @param {(key: string, options?: object) => string} params.t Übersetzungsfunktion
 * @param {RevaLanguage} params.language
 * @param {boolean} params.hasParamConflicts ob überhaupt Konflikte bestehen
 * @param {string[]} params.conflictedParameters Schlüssel der Parameter mit ungelöstem Konflikt
 * @param {Record<string, {nameDe?: string, nameEn?: string}>} params.segmentInputParams Spec des aktuellen Segments
 * @param {boolean} params.hasPersistenceError
 * @return {string} Zusatztext, leer wenn nichts blockiert
 */
function buildBlockedReason({
    t,
    language,
    hasParamConflicts,
    conflictedParameters,
    segmentInputParams,
    hasPersistenceError,
}) {
    if (!hasParamConflicts && !hasPersistenceError) {
        return "";
    }
    const conflictNames = conflictedParameters.map((parameter) => {
        const definition = segmentInputParams[parameter];
        return selectByLanguage(language, definition?.nameDe, definition?.nameEn) ?? parameter;
    });
    return (
        ` – ${t("object.error.impossible")}` +
        (hasParamConflicts ? ` ${t("object.error.unresolvedConflict")}: ${conflictNames.join("; ")}` : "") +
        (hasPersistenceError ? ` ${t("object.error.persistence")} ` : "")
    );
}

/**
 * Toolbar for Value page.
 * @param {string} appreciationKeyTab currently selected tab key for appreciation
 * @param {boolean} appreciationForTabVisible
 * @param {(boolean) => void} setAppreciationForTabVisible
 */
function ValueToolbar({appreciationKeyTab, appreciationForTabVisible, setAppreciationForTabVisible}) {
    const {t, language} = useRevaTranslation();
    const dispatch = useDispatch();

    const toast = useRef(null);

    const [finalizeDialogVisible, setFinalizeDialogVisible] = useState(false);
    const [preFinalizeDialogVisible, setPreFinalizeDialogVisible] = useState(false);
    const [correctionDialogVisible, setCorrectionDialogVisible] = useState(false);
    const lifecycleMenuRef = useRef(null);
    const pdfPreviewMenuRef = useRef(null);
    const [simplePdfLoading, setSimplePdfLoading] = useState(false);
    const [fullpdfLoading, setFullPdfLoading] = useState(false);
    const [tempPdfLoading, setTempPdfLoading] = useState(false);
    // Key "<snapshotNumber>/<docPath>" of the snapshot PDF currently being downloaded (or null).
    const [loadingSnapshotPdf, setLoadingSnapshotPdf] = useState(null);

    const revaId = useSelector(selectRevaId);
    const finalized = useSelector(selectLifeCycleStateFinal);
    const readOnly = useSelector(selectReadOnly);
    const isSnapshot = useSelector(selectIsSnapshot);
    // #19481: Research-Abfragen kennen kein (vorläufiges) Finalisieren, keine Korrektur und keine Gutachten-PDFs.
    const {finalizationAllowed} = useSelector(selectPurposePolicy);
    const canFinalize = useCheckPermission(PERMISSION_RULE_FINALIZATION) && finalizationAllowed;
    const canPreFinalize = useCheckPermission(PERMISSION_RULE_PRE_FINALIZATION) && finalizationAllowed;
    const canCorrect = useCheckPermission(PERMISSION_RULE_CORRECTION) && finalizationAllowed;
    // Single source for the permission→document-type mapping (shared with the dialogs).
    const allowedDocTypes = useAllowedArchiveDocumentTypes();
    const canRequestDocLimitedScope = allowedDocTypes.some((o) => o.docPath === "simple-valuation");
    const canRequestDocFullBelwertv = allowedDocTypes.some((o) => o.docPath === "valuation");
    const canRequestDocFullPfandbg = allowedDocTypes.some((o) => o.docPath === "full-valuation");

    const persistenceError = useSelector(selectPersistenceSingleParamError);
    const anyValueAvailable = useSelector(selectAnyValueAvailable);
    const hasParamConflicts = useSelector(selectHasAnyParamConflicts);
    const currentParamConflicts = useSelector(selectParameterConflicts);

    const externalOrderNumber = useSelector(selectMostRelevantExternalOrderNumber);
    const correctionCounter = useSelector(selectCorrectionCounter);
    const valuationMetadata = useSelector(selectValuationMetadata);
    const lifeCycleState = useSelector(selectLifeCycleState);
    const snapshots = useSelector(selectSnapshots);
    const loadedSnapshotNumber = useSelector(selectLoadedSnapshotNumber);
    const currentUsername = useSelector(selectUsername);
    const documents = useSelector(selectDocuments);
    const segment = useSelector(selectSegment);
    const segmentInputParams = useSelector(selectApiInputParamsForCurrentSegment);

    const pdfSupported = isPdfSupported(segment) && finalizationAllowed;
    const pdfGenerationDisabled = !anyValueAvailable;
    const correctionSuffix = correctionCounter ? `_Rev${correctionCounter}` : "";

    // Fallback filename, if the Download response does not include a Content-Disposition header with a filename
    const pdfFallbackFilename = (baseName) => {
        const preview = !finalized;
        const date = new Date().toISOString().substring(0, 10);
        const identifier = externalOrderNumber || revaId;
        const previewSuffix = preview ? "_Vorschau" : "";
        const filename = `${date}_${identifier}_${baseName}${correctionSuffix}${previewSuffix}.pdf`;
        console.warn(`PDF download response did not include a filename! Using default filename '${filename}' instead.`);
        return filename;
    };

    /**
     * Show PDF error toast based on error code from PDF service.
     * @param {Error|WebError} error the caught error
     */
    const showPdfError = (error) => {
        let detail = t("value.pdf.error.generic");

        if (error instanceof WebError) {
            const errorCode = error.getErrorCode();
            const errorData = error.getCustomErrorData();

            if (errorCode && t(`value.pdf.error.${errorCode}`) !== `value.pdf.error.${errorCode}`) {
                // Use translated error message for known error codes
                detail = t(`value.pdf.error.${errorCode}`);
            } else if (errorData) {
                // Use localized error detail from service response
                detail = getErrorDetail(errorData, language) || detail;
            }
        }

        toast.current.show({
            severity: "error",
            summary: t("value.pdf.error.title"),
            detail,
            life: 5000,
        });
    };

    const findFinalizedDocumentFor = (nameKey) => {
        if (!finalized) return null;
        return findFinalizedDocument(documents, {
            nameKey,
            externalOrderNumber,
            finalizeDate: valuationMetadata?.finalizeDate,
            correctionCounter,
        });
    };

    const downloadPdf = async (setLoading, nameKey, pdfType) => {
        setLoading(true);
        try {
            const doc = findFinalizedDocumentFor(nameKey);
            if (doc) {
                const blob = await dispatch(downloadDocumentAsync(doc));
                openBlobForDownload(blob, doc.filename);
            } else {
                const {blob, filename} = await dispatch(fetchValuationPdf(pdfType));
                openBlobForDownload(blob, filename || pdfFallbackFilename(nameKey));
            }
        } catch (e) {
            console.error(e);
            showPdfError(e);
        } finally {
            setLoading(false);
        }
    };

    const onDownloadPdf = () =>
        downloadPdf(
            setSimplePdfLoading,
            "VereinfachteWertermittlung-24BelWertV-ausfuehrlich",
            ValuationPdfType.VALUATION,
        );
    const onDownloadFullPdf = () => downloadPdf(setFullPdfLoading, "Gutachten", ValuationPdfType.FULL_VALUATION);

    // Erzeugt anders als die beiden anderen Vorschauen immer ein frisches PDF und greift nicht auf
    // ein bereits archiviertes Dokument der Finalisierung zurück.
    const onDownloadTempPdf = async () => {
        setTempPdfLoading(true);
        try {
            const {blob, filename} = await dispatch(fetchValuationPdf(ValuationPdfType.SIMPLE_VALUATION));
            openBlobForDownload(blob, filename || pdfFallbackFilename("VereinfachteWertermittlung-24BelWertV-kompakt"));
        } catch (e) {
            console.error(e);
            showPdfError(e);
        } finally {
            setTempPdfLoading(false);
        }
    };

    const blockedReason = buildBlockedReason({
        t,
        language,
        hasParamConflicts,
        conflictedParameters: currentParamConflicts,
        segmentInputParams,
        hasPersistenceError: !!persistenceError,
    });

    // Congratulatory growl shown after a (pre-)finalize that released the current user's ownership
    // (the valuation is done from their side and handed back to the pool). The dialogs only call this
    // when the valuation was actually assigned to the current user before the action.
    const notifyHandedOff = () => {
        toast.current?.show({
            severity: "success",
            summary: t("value.actions.handoffToastSummary"),
            detail: t("value.actions.handoffToastDetail"),
            life: 6000,
        });
    };

    const openFinalizeDialog = () => {
        if (!revaId) return;
        setFinalizeDialogVisible(true);
    };

    const openPreFinalizeDialog = () => {
        if (!revaId) return;
        setPreFinalizeDialogVisible(true);
    };

    const openCorrectionDialog = () => {
        if (!revaId) return;
        setCorrectionDialogVisible(true);
    };

    // Lifecycle transitions. Pre-finalize / finalize actions can't run while the
    // valuation is currently final; correction is offered once it has reached >= Final.
    const lifecycleActionsDisabled = !anyValueAvailable || !!persistenceError || hasParamConflicts;
    const transitionDisabled = readOnly || lifecycleActionsDisabled;

    // Compact submenu label: today's snapshots show only the time, older ones only the
    // date (never both); the current user is shortened to "von mir" / "by me".
    const snapshotInfoLabel = (baseKey, snapshot) => {
        const user =
            snapshot.user === currentUsername ? t("value.actions.byYou") : (snapshot.userFullName ?? snapshot.user);
        return isSameDay(snapshot.date)
            ? t(`${baseKey}AtTime`, {time: formatTime(snapshot.date, language), user})
            : t(`${baseKey}At`, {date: formatDate(snapshot.date, language), user});
    };

    const onDownloadSnapshotPdf = async (snapshot, docPath, nameKey) => {
        setLoadingSnapshotPdf(`${snapshot.number}/${docPath}`);
        try {
            const {blob, filename} = await dispatch(fetchSnapshotPdf(snapshot.number, docPath));
            openBlobForDownload(blob, filename || pdfFallbackFilename(nameKey));
        } catch (e) {
            console.error(e);
            showPdfError(e);
        } finally {
            setLoadingSnapshotPdf(null);
        }
    };

    // Reset + load handlers (snapshot / live), shared with the snapshot banner.
    const {loadSnapshot, loadLiveData} = useLoadValuationState();
    // Whether the live data state may be loaded for the current user (+ matching label),
    // by the same rules as the snapshot banner's "continue" link. Null if not available.
    const liveDataEntry = useLiveDataEntry();
    // Whether a correction may be started from the snapshot view (newest snapshot of a finalized
    // valuation) — same rule as the snapshot banner's "Korrektur starten" link.
    const canStartCorrection = useCanStartCorrection();

    // PDF download entry inside a snapshot submenu. PrimeReact TieredMenu items have no native
    // loading state, so a loading entry is disabled and shows a spinner instead of its icon.
    const snapshotPdfItem = (snapshot, labelKey, docPath, nameKey) => {
        const loading = loadingSnapshotPdf === `${snapshot.number}/${docPath}`;
        return {
            label: t(labelKey),
            icon: loading ? "pi pi-spinner pi-spin" : PrimeIcons.FILE_PDF,
            disabled: loading,
            command: () => onDownloadSnapshotPdf(snapshot, docPath, nameKey),
            // Used for the collapsed single-button tooltip, e.g.
            // "Vereinfachte Wertermittlung § 24 BelWertV – kompakt der vorläufigen Finalisierung anzeigen".
            buttonTooltip: t(
                snapshot.type === SnapshotType.Final
                    ? "value.actions.snapshotPdfShowFinal"
                    : "value.actions.snapshotPdfShowPreFinal",
                {docType: t(labelKey)},
            ),
        };
    };

    // Menu entry for one PreFinal/Final snapshot. Always a submenu: the "load data state"
    // action on top and — separated by a divider — the PDFs the user may request below it.
    const snapshotSubmenu = (snapshot) => {
        const isFinalSnapshot = snapshot.type === SnapshotType.Final;
        const pdfItems = allowedDocTypes.map((o) => snapshotPdfItem(snapshot, o.labelKey, o.docPath, o.nameKey));
        const label = snapshotInfoLabel(
            isFinalSnapshot ? "value.actions.finalized" : "value.actions.preFinalized",
            snapshot,
        );
        // Loads this snapshot into REVA (reset + load). While it is already the loaded data
        // state the entry is disabled and shows an alternative label.
        const alreadyLoaded = loadedSnapshotNumber === snapshot.number;
        const loadItem = {
            label: t(alreadyLoaded ? "value.actions.snapshotAlreadyLoaded" : "value.actions.loadSnapshot"),
            icon: PrimeIcons.HISTORY,
            disabled: alreadyLoaded,
            command: () => loadSnapshot(snapshot.number),
        };
        return {
            label,
            icon: isFinalSnapshot ? PrimeIcons.LOCK : PrimeIcons.FLAG,
            items: pdfItems.length > 0 ? [loadItem, {separator: true}, ...pdfItems] : [loadItem],
        };
    };

    // The menu reads like a timeline from top to bottom: what has already happened (one submenu
    // per PreFinal/Final snapshot, chronological, oldest first), then — while a snapshot is
    // loaded and the user may load it — the entry to return to the live data state, then the
    // possible next steps (pre-finalize before finalize), correction always last.
    const lifecycleMenuItems = [];
    // Every snapshot entry has at least the "load data state" action, so none are empty.
    const snapshotEntries = (snapshots ?? [])
        .filter((s) => s.type === SnapshotType.PreFinal || s.type === SnapshotType.Final)
        .toSorted((a, b) => Date.parse(a.date) - Date.parse(b.date))
        .map(snapshotSubmenu);
    lifecycleMenuItems.push(...snapshotEntries);
    // Below the snapshots: while viewing one, offer returning to the live (editable) data state —
    // but only when the current user may load it (per live lifecycle state + permissions), with a
    // label matching the situation ("Datenerfassung fortsetzen" / "Korrektur ... fortsetzen").
    if (isSnapshot && liveDataEntry) {
        lifecycleMenuItems.push({
            label: t(liveDataEntry.labelKey),
            icon: PrimeIcons.FILE_EDIT,
            command: loadLiveData,
        });
    }
    let hasTransitionEntry = false;
    // Pre-finalize: only possible ONCE — the entry is offered only while the valuation
    // has not yet reached PreFinal (#18860).
    if (pdfSupported && canPreFinalize && !isLifeCycleAtLeast(lifeCycleState, RevaLifeCycleState.PreFinal)) {
        hasTransitionEntry = true;
        lifecycleMenuItems.push({
            label: t("value.actions.preFinalize"),
            icon: PrimeIcons.FLAG,
            disabled: transitionDisabled,
            command: openPreFinalizeDialog,
        });
    }
    // Finalize: offered only as long as there has been no finalization at all, or — once
    // finalized at least once — only while the valuation is in correction (re-finalize).
    const hasAnyFinalization = finalized || !!valuationMetadata?.firstFinalizeDate;
    if (pdfSupported && canFinalize && (!hasAnyFinalization || lifeCycleState === RevaLifeCycleState.Correction)) {
        hasTransitionEntry = true;
        lifecycleMenuItems.push({
            label: t("value.actions.finalize"),
            icon: PrimeIcons.LOCK,
            disabled: transitionDisabled,
            command: openFinalizeDialog,
        });
    }
    // Correction: a single action always at the bottom, available only while the valuation is
    // currently final — the backend rejects reopening otherwise (multiple corrections are
    // possible, but each one requires re-finalizing first). Offered in the same situations as
    // the snapshot banner's "Korrektur starten" link: on the newest snapshot of a finalized
    // valuation (canStartCorrection), ...
    const hasCorrectionEntry =
        canStartCorrection ||
        // ... or on the live finalized valuation. TODO(#18973): drop this legacy branch (kept for
        // backwards compatibility) once live states of finalized valuations can no longer be loaded.
        (canCorrect && finalized && !isSnapshot);
    if (hasCorrectionEntry) {
        lifecycleMenuItems.push({
            label: t("value.actions.correction"),
            icon: PrimeIcons.REPLAY,
            command: openCorrectionDialog,
        });
    }

    // The menu-button tooltip describes what the menu OFFERS, not the current state:
    // history + correction, history + (re-)finalizing, history only, or just finalizing.
    const lifecycleMenuTooltip = loadingSnapshotPdf
        ? t("value.actions.snapshotPdfLoading")
        : (hasCorrectionEntry
              ? t("value.actions.lifecycleMenuHistoryOrCorrection")
              : snapshotEntries.length > 0 && hasTransitionEntry
                ? t("value.actions.lifecycleMenuFinalizations")
                : snapshotEntries.length > 0
                  ? t("value.actions.lifecycleMenuHistory")
                  : t("value.actions.lifecycleMenuFinalize")) + blockedReason;

    // The single-button shortcut only applies to a lone DIRECT action (pre-/finalize or
    // correction). A lone snapshot entry is always a submenu (load + PDF actions) and is
    // therefore shown via the menu, never as a direct button.
    const singleDirectAction = lifecycleMenuItems.length === 1 && !lifecycleMenuItems[0].items;
    const showLifecycleMenu = lifecycleMenuItems.length > 0 && !singleDirectAction;

    // Entry of the PDF preview menu. PrimeReact TieredMenu items have no native loading
    // state, so a loading entry is disabled and shows a spinner instead of its icon.
    const pdfPreviewMenuItem = (labelKey, loading, command) => ({
        label: t(labelKey),
        icon: loading ? "pi pi-spinner pi-spin" : PrimeIcons.FILE_PDF,
        disabled: loading,
        command,
    });

    // While a PDF is being generated, the (closed) menu's spinner is invisible to the
    // user — so the menu button itself also spins until the download opens.
    const anyPdfPreviewLoading = fullpdfLoading || simplePdfLoading || tempPdfLoading;

    // Per-type loading flags and click handlers of the classic preview buttons.
    const pdfPreviewByDocPath = {
        "full-valuation": {loading: fullpdfLoading, command: onDownloadFullPdf},
        valuation: {loading: simplePdfLoading, command: onDownloadPdf},
        "simple-valuation": {loading: tempPdfLoading, command: onDownloadTempPdf},
    };
    const pdfPreviewMenuItems = allowedDocTypes.map((o) =>
        pdfPreviewMenuItem(o.labelKey, pdfPreviewByDocPath[o.docPath].loading, pdfPreviewByDocPath[o.docPath].command),
    );
    // PDF preview downloads: with two or more permitted document types they are collected
    // in a single "PDF Vorschau" menu button; with exactly one, the classic single button
    // for that type is shown instead (see JSX below). While a frozen snapshot is loaded,
    // no live preview PDF can be generated — snapshot PDFs are offered via the lifecycle
    // menu instead (#18973).
    const showPdfPreviewMenu = pdfSupported && !isSnapshot && pdfPreviewMenuItems.length >= 2;
    const showSinglePdfButton = pdfSupported && !isSnapshot && pdfPreviewMenuItems.length === 1;

    return (
        <div className="HeaderToolbarRight">
            <Toast ref={toast} />

            {appreciationKeyTab && (
                <AppreciationToolbarButton
                    page="value"
                    tab={appreciationKeyTab}
                    classNameMargin="mr-4"
                    appreciationVisible={appreciationForTabVisible}
                    setAppreciationVisible={setAppreciationForTabVisible}
                />
            )}

            {/* A lone DIRECT action (pre-/finalize or correction) is shown without a menu: the
                button executes the entry directly and its tooltip carries the entry's label (e.g. a
                pre-finalize-only user gets a direct "Bewertung vorläufig abschließen" button). A lone
                snapshot entry is a submenu and is shown via the menu below instead. */}
            {singleDirectAction && (
                <span id="lifecycleActionButtonWrapper">
                    <Button
                        id="lifecycleActionButton"
                        icon={lifecycleMenuItems[0].icon}
                        loading={!!loadingSnapshotPdf}
                        disabled={lifecycleMenuItems[0].disabled || !!loadingSnapshotPdf}
                        onClick={() => lifecycleMenuItems[0].command?.()}
                        tooltip={
                            loadingSnapshotPdf
                                ? t("value.actions.snapshotPdfLoading")
                                : (lifecycleMenuItems[0].buttonTooltip ?? lifecycleMenuItems[0].label) +
                                  (lifecycleMenuItems[0].disabled ? blockedReason : "")
                        }
                        tooltipOptions={{position: "top", showOnDisabled: true}}
                    />
                </span>
            )}
            {showLifecycleMenu && (
                <>
                    <TieredMenu
                        id="lifecycleMenu"
                        model={lifecycleMenuItems}
                        popup
                        ref={lifecycleMenuRef}
                        // Twice the PrimeReact default (12.5rem): the snapshot labels carry date + user.
                        style={{width: "25rem"}}
                    />
                    {/* Wrapper, damit die Tooltip-Option showOnDisabled=true beim Re-Rendern keine Fehler verursacht */}
                    <span id="lifecycleMenuButtonWrapper">
                        <Button
                            id="lifecycleMenuButton"
                            icon={PrimeIcons.LOCK}
                            loading={!!loadingSnapshotPdf}
                            disabled={!!loadingSnapshotPdf}
                            onClick={(e) => lifecycleMenuRef.current?.toggle(e)}
                            aria-controls="lifecycleMenu"
                            aria-haspopup
                            tooltip={lifecycleMenuTooltip}
                            tooltipOptions={{position: "top", showOnDisabled: true}}
                        />
                    </span>
                </>
            )}

            <DialogFinalizeValuation
                visible={finalizeDialogVisible}
                setVisible={setFinalizeDialogVisible}
                onHandedOff={notifyHandedOff}
            />

            <DialogPreFinalizeValuation
                visible={preFinalizeDialogVisible}
                setVisible={setPreFinalizeDialogVisible}
                onHandedOff={notifyHandedOff}
            />

            <ReopenForCorrectionDialog
                visible={correctionDialogVisible}
                onHide={() => setCorrectionDialogVisible(false)}
            />

            {showPdfPreviewMenu && (
                <>
                    <TieredMenu
                        id="pdfPreviewMenu"
                        model={pdfPreviewMenuItems}
                        popup
                        ref={pdfPreviewMenuRef}
                        style={{width: "18rem"}}
                    />
                    {/* Wrapper, damit die Tooltip-Option showOnDisabled=true beim Re-Rendern keine Fehler verursacht */}
                    <span id="pdfPreviewMenuButtonWrapper">
                        <Button
                            id="pdfPreviewMenuButton"
                            className="ml-4"
                            icon={PrimeIcons.FILE_PDF}
                            loading={anyPdfPreviewLoading}
                            disabled={pdfGenerationDisabled || anyPdfPreviewLoading}
                            onClick={(e) => pdfPreviewMenuRef.current?.toggle(e)}
                            aria-controls="pdfPreviewMenu"
                            aria-haspopup
                            tooltip={t(
                                anyPdfPreviewLoading
                                    ? "value.actions.pdfPreviewMenuLoading"
                                    : "value.actions.pdfPreviewMenu",
                            )}
                            tooltipOptions={{
                                position: "top",
                                my: "right bottom",
                                at: "right top",
                                showOnDisabled: true,
                                pt: {
                                    arrow: {style: {left: "auto", right: "1.25rem"}},
                                    text: {style: {whiteSpace: "nowrap"}},
                                },
                            }}
                        />
                    </span>
                </>
            )}

            {showSinglePdfButton && canRequestDocFullPfandbg && (
                <Button
                    id={"fullvaluation-button"}
                    disabled={pdfGenerationDisabled || fullpdfLoading}
                    className="ml-4"
                    loading={fullpdfLoading}
                    icon={PrimeIcons.FILE_PDF}
                    style={{fontWeight: "bold"}}
                    tooltip={
                        finalized ? t("value.actions.fullValuationPdf") : t("value.actions.fullValuationPdfPreview")
                    }
                    tooltipOptions={{position: "left"}}
                    onClick={onDownloadFullPdf}
                />
            )}

            {showSinglePdfButton && canRequestDocFullBelwertv && (
                <Button
                    disabled={pdfGenerationDisabled || simplePdfLoading}
                    className="ml-4"
                    loading={simplePdfLoading}
                    icon={PrimeIcons.FILE_PDF}
                    tooltip={finalized ? t("value.actions.pdf") : t("value.actions.pdfPreview")}
                    tooltipOptions={{position: "left"}}
                    onClick={onDownloadPdf}
                />
            )}

            {showSinglePdfButton && canRequestDocLimitedScope && (
                <Button
                    disabled={pdfGenerationDisabled || tempPdfLoading}
                    className="ml-4"
                    loading={tempPdfLoading}
                    icon={PrimeIcons.FILE}
                    tooltip={finalized ? t("value.actions.pdfWithoutImage") : t("value.actions.pdfPreviewWithoutImage")}
                    tooltipOptions={{position: "left"}}
                    onClick={onDownloadTempPdf}
                />
            )}
        </div>
    );
}

export default ValueToolbar;
